import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import UtmTracker from '@/components/UtmTracker'
import DynamicBreadcrumb from '@/components/DynamicBreadcrumb'
import useScrollToTop from '@/hooks/useScrollToTop'
import useFaqs from '@/hooks/useFaqs.jsx'
import ClaimScenarios from '@/components/ClaimScenarios'
import USPBanner from '@/components/USPBanner'
import MisSoldSection from '@/components/MisSoldSection'
import ClaimCheckBanner from '@/components/ClaimCheckBanner'
import MisSoldSigns from '@/components/MisSoldSigns'
import KeyInformation from '@/components/KeyInformation'
import FAQ from '@/components/FAQ'
import MarkdownPage from '@/components/MarkdownPage'
import ClaimUpdatesPage from '@/components/ClaimUpdatesPage'
import AboutPage from '@/components/AboutPage'
import BlogsPage from '@/components/BlogsPage'
import BlogPost1 from '@/components/BlogPost1'
import BlackHorsePage from '@/components/BlackHorsePage'
import AlpheraFinancePage from '@/components/AlpheraFinancePage'
import BMWFinancePage from '@/components/BMWFinancePage'
import SantanderPage from '@/components/SantanderPage'
import MercedesFinancePage from '@/components/MercedesFinancePage'
import ToyotaFinancePage from '@/components/ToyotaFinancePage'
import VolkswagenFinancePage from '@/components/VolkswagenFinancePage'
import AudiFinancePage from '@/components/AudiFinancePage'
import HondaFinancePage from '@/components/HondaFinancePage'
import BarclaysFinancePage from '@/components/BarclaysFinancePage'
import BlueMotorFinancePage from '@/components/BlueMotorFinancePage'
import CAAutoFinancePage from '@/components/CAAutoFinancePage'
import CloseBrothersFinancePage from '@/components/CloseBrothersFinancePage'
import ClydesdaleBankPage from '@/components/ClydesdaleBankPage'
import HyundaiFinancePage from '@/components/HyundaiFinancePage'
import MoneybarnPage from '@/components/MoneybarnPage'
import MotonovoPage from '@/components/MotonovoPage'
import NorthridgePage from '@/components/NorthridgePage'
import PSAFinancePage from '@/components/PSAFinancePage'
import VauxhallFinancePage from '@/components/VauxhallFinancePage'
import VehiclePage from '@/components/VehiclePage'
import FAQsPage from '@/components/FAQsPage'
import ContactPage from '@/components/ContactPage'
import CustomerReviewPage from '@/components/CustomerReviewPage'
import ReferralProgram from '@/components/ReferralProgram'
import EgenticPage from '@/components/EgenticPage'
import CTAButton from '@/components/shared/CTAButton'
import TrustpilotBadge from '@/components/TrustpilotBadge'
import SEO from '@/components/shared/SEO'

const HomePage = () => {
  // Load first 5 FAQs for homepage
  const { faqs } = useFaqs(5)

  return (
    <>
      <SEO
        title="Car Finance Claims UK - Check Eligibility in 60 Seconds | Your Claim Matters"
        description="Were you mis-sold car finance? Check if you're owed compensation in just 60 seconds. Average payout £2,086 per household. Start your claim today."
        keywords="car finance claims, mis-sold car finance, PCP claims, HP claims, car finance compensation, Your Claim Matters"
        url="/"
        image="/Image/Car-photo-illustration-claims1.webp"
        imageAlt="Car finance claims - Your Claim Matters services"
        schemaType="WebPage"
        schemaData={{
          mainEntity: {
            '@type': 'Service',
            name: 'Car Finance Claims Service',
            description:
              'Professional car finance mis-selling claims service helping UK consumers recover compensation from mis-sold finance agreements.',
            provider: {
              '@type': 'Organization',
              name: 'Your Claim Matters',
              url: 'https://yourclaimmatters.co.uk',
            },
            areaServed: 'United Kingdom',
            serviceType: 'Legal Services',
          },
        }}
        breadcrumbs={[{ name: 'Home', url: '/' }]}
        faq={true}
        faqs={faqs}
      />
      <USPBanner />
      <section className="flex min-h-[auto] w-full items-center bg-gray-100 pt-0 pb-12 lg:min-h-[90vh]">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-4 px-4 lg:grid-cols-2 lg:gap-6 lg:py-12">
          {/* Left Column - Content */}
          <div className="flex flex-col items-center space-y-6 text-center">
            <h1 className="text-foreground mb-4 pt-12 text-center text-4xl font-black sm:mb-6 sm:text-6xl lg:pt-0">
              DID YOU PURCHASE <br />
              A MOTOR VEHICLE <br />
              ON FINANCE AFTER 2007?
            </h1>
            <p className="font-raleway-numbers text-base leading-relaxed font-bold text-neutral-700 sm:text-lg lg:text-3xl">
              You could be owed around £829 per claim
              <sup style={{ fontSize: '0.4em', verticalAlign: 'super' }}>4</sup>
              ,
              <br />
              No win, no fee
              <sup style={{ fontSize: '0.4em', verticalAlign: 'super' }}>3</sup>
              .
            </p>
            <div className="flex flex-col items-center space-y-4">
              <CTAButton />
              <div>
                <strong className="text-lg font-black tracking-wide text-neutral-900 md:text-xl">
                  MAKE YOUR CLAIM IN{' '}
                  <span className="font-raleway-numbers">60</span> SECONDS
                </strong>
              </div>
              <div>
                <TrustpilotBadge rating="4star" />
              </div>
            </div>
          </div>

          {/* Right Column - Car Image */}
          <div className="hidden items-center justify-center lg:flex lg:justify-end">
            <img
              src="/Image/ycm-homepage-hero-car.png"
              alt="Car finance claims"
              className="h-auto max-w-full mix-blend-multiply"
              width="780"
              height="520"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>
      <ClaimScenarios />
      <MisSoldSection />
      <ClaimCheckBanner />
      <MisSoldSigns />

      <FAQ faqs={faqs} includeStructuredData={false} />
      <KeyInformation />
    </>
  )
}

// Component that uses the scroll-to-top hook inside Router context
export const AppContent = () => {
  useScrollToTop()

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <UtmTracker />
      <Header />
      <DynamicBreadcrumb />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about-us" element={<AboutPage />} />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route
          path="/blogs/data-deletion-threatens-1bn-car-finance-compensation-uk-borrowers"
          element={<BlogPost1 />}
        />
        <Route path="/faqs" element={<FAQsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/customer-review" element={<CustomerReviewPage />} />
        <Route path="/referral-program" element={<ReferralProgram />} />
        <Route path="/egentic" element={<EgenticPage />} />
        <Route
          path="/lenders/mis-sold-black-horse-car-finance"
          element={<BlackHorsePage />}
        />
        <Route
          path="/lenders/mis-sold-alphera-car-finance"
          element={<AlpheraFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-audi-car-finance"
          element={<AudiFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-barclays-car-finance"
          element={<BarclaysFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-blue-motor-car-finance"
          element={<BlueMotorFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-bmw-car-finance"
          element={<BMWFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-ca-auto-car-finance"
          element={<CAAutoFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-close-brothers-car-finance"
          element={<CloseBrothersFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-clydesdale-bank-car-finance"
          element={<ClydesdaleBankPage />}
        />
        <Route
          path="/lenders/mis-sold-honda-car-finance"
          element={<HondaFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-hyundai-car-finance"
          element={<HyundaiFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-mercedes-car-finance"
          element={<MercedesFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-moneybarn-car-finance"
          element={<MoneybarnPage />}
        />
        <Route
          path="/lenders/mis-sold-motonovo-car-finance"
          element={<MotonovoPage />}
        />
        <Route
          path="/lenders/mis-sold-northridge-car-finance"
          element={<NorthridgePage />}
        />
        <Route
          path="/lenders/mis-sold-psa-car-finance"
          element={<PSAFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-santander-car-finance"
          element={<SantanderPage />}
        />
        <Route
          path="/lenders/mis-sold-toyota-car-finance"
          element={<ToyotaFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-vauxhall-car-finance"
          element={<VauxhallFinancePage />}
        />
        <Route
          path="/lenders/mis-sold-volkswagen-car-finance"
          element={<VolkswagenFinancePage />}
        />
        <Route path="/vehicles/audi-car-finance" element={<VehiclePage />} />
        <Route path="/vehicles/:make" element={<VehiclePage />} />
        <Route path="/claim-updates" element={<ClaimUpdatesPage />} />
        <Route path="/:page" element={<MarkdownPage />} />
      </Routes>
      <Footer />
    </div>
  )
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App
